/**
 * GlobalErrorHandler
 * ------------------
 * Last line of defense against the "blank Android WebView" symptom.
 *
 * Background
 * ----------
 * Capacitor wraps the React webapp in a native WebView. When the bundle
 * throws an unhandled error — typical culprits are:
 *   - a SyntaxError in a lazily-loaded chunk that the WebView refuses to
 *     evaluate after a stale-cache update,
 *   - a Capacitor plugin / SQLite / Firebase top-level import failing,
 *   - an unhandled `Promise` rejection from a fire-and-forget call,
 *   - the React tree throwing an error that the ErrorBoundary itself
 *     fails to render (very rare but possible — typically because the
 *     fallback depends on i18n/styled-components and those threw too),
 * — the WebView ends up showing a blank page and the user cannot recover
 * without force-quitting and reopening the app. Native crash reporting
 * does not fire because, from Android's point of view, the WebView is
 * still alive and "rendered fine"; the content is just empty.
 *
 * What this module does
 * ---------------------
 *   1. Registers `window.error` and `unhandledrejection` listeners at the
 *      very top of the JS bundle (this module is imported as the FIRST
 *      thing in `src/index.js`, before any other module is evaluated, so
 *      a failure inside `FirebaseUtils` or `App.js` is still observed).
 *   2. Forwards every captured error to:
 *        - `console.error` (visible in Chrome DevTools when remote
 *          debugging the Android WebView),
 *        - the native logger via the existing `DiagnosticLogsPlugin`
 *          Capacitor plugin (so it ends up in WSLogger / logcat /
 *          DataDog, regardless of whether the webapp's UI is still up),
 *        - `window.DD_LOGS.logger.error` if Datadog has been initialised.
 *   3. Detects "WebView is blank" — i.e. `#root` is empty or has zero
 *      height — and paints a minimal DOM fallback with a short error
 *      excerpt, a "Restart" button (soft reload of the WebView), and,
 *      after a repeated failure, a "Reset app data" button that clears
 *      caches before reload.
 *   4. Uses `sessionStorage` to count consecutive restarts in a 30-second
 *      window. If the app gets stuck in a reload loop, the user sees the
 *      "Reset app data" option immediately instead of restarting again.
 *   5. Also acts as a safety-net: if the React tree has not painted
 *      anything within 7s of load, we assume the bundle failed silently
 *      and render the fallback so the user is never stranded.
 *
 * Design constraint: this module deliberately has NO dependencies on any
 * other webapp module. It must keep working when everything else is on
 * fire — so it imports only from `@capacitor/core` (which is statically
 * pre-bundled by Capacitor itself and ~never throws at import time).
 */

import {Capacitor, registerPlugin} from "@capacitor/core";

const STORAGE_KEYS = {
  count: "ws.gh.restartCount",
  ts: "ws.gh.restartTs"
};

// If two restarts happen within this window we treat them as a loop and
// surface the "Reset app data" CTA immediately.
const LOOP_WINDOW_MS = 30000;
const MAX_RESTARTS_IN_LOOP = 3;

// How long we wait for the React tree to paint anything before assuming
// the bundle is broken and showing the fallback.
const BLANK_SCREEN_WATCHDOG_MS = 7000;

// After the fallback is shown, how often and for how long we check whether the app painted.
const RECOVERY_POLL_MS = 1000;
const RECOVERY_WATCH_MS = 120000;

let installed = false;
let fallbackRendered = false;
let pendingNativePush = Promise.resolve();
let diagnosticPlugin: any = null;

const isAndroid = (() => {
  try {
    return Capacitor.isNativePlatform();
  } catch {
    return false;
  }
})();

function getDiagnosticPlugin() {
  if (diagnosticPlugin) return diagnosticPlugin;
  try {
    diagnosticPlugin = registerPlugin("DiagnosticLogsPlugin");
  } catch {
    diagnosticPlugin = null;
  }
  return diagnosticPlugin;
}

function pushNative(message: string) {
  if (!isAndroid) return Promise.resolve();
  const plugin = getDiagnosticPlugin();
  if (!plugin || typeof plugin.pushLog !== "function") return Promise.resolve();
  // Serialize pushes so a synchronous burst of errors doesn't race the
  // native bridge. Always swallow errors here — this helper must never
  // throw because it runs inside an error handler.
  pendingNativePush = pendingNativePush
    .catch(() => {})
    .then(() => plugin.pushLog({message: `[GlobalError] ${message}`}).catch(() => {}));
  return pendingNativePush;
}

function pushDatadog(level: string, message: string, extras?: any) {
  try {
    const logger = typeof window !== "undefined" ? (window as any).DD_LOGS?.logger : null;
    if (logger && typeof logger[level] === "function") {
      logger[level](message, extras);
    }
  } catch {
    // Datadog is best-effort.
  }
}

function readRestartCount() {
  try {
    const now = Date.now();
    const last = Number(sessionStorage.getItem(STORAGE_KEYS.ts)) || 0;
    if (now - last > LOOP_WINDOW_MS) {
      sessionStorage.setItem(STORAGE_KEYS.count, "0");
    }
    return Number(sessionStorage.getItem(STORAGE_KEYS.count)) || 0;
  } catch {
    return 0;
  }
}

function recordRestart() {
  try {
    const count = readRestartCount() + 1;
    sessionStorage.setItem(STORAGE_KEYS.count, String(count));
    sessionStorage.setItem(STORAGE_KEYS.ts, String(Date.now()));
  } catch {
    // sessionStorage might be unavailable (very old WebViews); just skip
    // the loop guard rather than break the recovery path.
  }
}

function rootIsBlank() {
  try {
    const root = document.getElementById("root");
    if (!root) return true;
    if (!root.children || root.children.length === 0) return true;
    // offsetHeight === 0 covers the case where React hydrated something
    // empty (e.g. an unstyled Fragment) and the layout has zero pixels.
    return root.offsetHeight === 0;
  } catch {
    return false;
  }
}

function clearCachesBestEffort() {
  try {
    if (typeof window !== "undefined" && window.caches && typeof window.caches.keys === "function") {
      window.caches
        .keys()
        .then((keys) => Promise.all(keys.map((k) => caches.delete(k))))
        .catch(() => {});
    }
  } catch {
    // ignore
  }
  try {
    sessionStorage.removeItem(STORAGE_KEYS.count);
    sessionStorage.removeItem(STORAGE_KEYS.ts);
  } catch {
    // ignore
  }
}

function softRestart() {
  recordRestart();
  setTimeout(() => {
    try {
      window.location.reload();
    } catch {
      // Last resort: navigate to the app root.
      try {
        window.location.href = window.location.origin || "/";
      } catch {
        // nothing else we can do
      }
    }
  }, 80);
}

function fullReset() {
  clearCachesBestEffort();
  // We intentionally do NOT clear localStorage here — that would log the
  // user out and wipe their queued local activities. The CTA is "reset
  // app caches" not "log out".
  setTimeout(() => {
    try {
      window.location.replace(window.location.origin || "/");
    } catch {
      try {
        window.location.reload();
      } catch {
        // nothing else we can do
      }
    }
  }, 80);
}

function escapeHtml(s: any) {
  return String(s == null ? "" : s)
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;");
}

function summarize(label: string, info: any) {
  const head = `${label}: ${info?.message || "unknown error"}`;
  const loc = info?.filename != null ? `\n@ ${info.filename}:${info?.lineno ?? "?"}:${info?.colno ?? "?"}` : "";
  const tail = info?.stack ? `\n${info.stack}` : "";
  return `${head}${loc}${tail}`;
}

function renderFallback(summary: string) {
  if (fallbackRendered) return;
  if (typeof document === "undefined" || !document.body) return;
  fallbackRendered = true;

  const restartCount = readRestartCount();
  const showResetCta = restartCount >= 1;

  const overlay = document.createElement("div");
  overlay.id = "global-error-fallback";
  overlay.setAttribute("data-testid", "global-error-fallback");
  overlay.style.cssText = [
    "position:fixed",
    "inset:0",
    "z-index:2147483647",
    "background:#ffffff",
    "color:#131313",
    "display:flex",
    "flex-direction:column",
    "align-items:center",
    "justify-content:center",
    "padding:24px",
    "font-family:Inter,-apple-system,BlinkMacSystemFont,'Segoe UI',Roboto,sans-serif",
    "text-align:center"
  ].join(";");

  overlay.innerHTML = `
    <img alt="Waterspeed" src="/assets/images/waterspeed-logo.png"
      style="width:96px;height:96px;margin-bottom:24px;"
      onerror="this.style.display='none'"/>
    <h1 style="font-size:1.25rem;font-weight:500;margin:0 0 8px;color:#131313;font-family:'Work Sans','Helvetica Neue',Arial,sans-serif;letter-spacing:-0.01em;">
      Something went wrong
    </h1>
    <p style="margin:0 0 20px;color:#5f6366;max-width:480px;font-size:0.95rem;line-height:1.4;">
      The app hit an unexpected error and needs to restart.
      Your activities and settings are safe.
    </p>
    <pre id="ge-summary" style="
      margin:0 0 20px;padding:12px;background:#f5f8fa;border-radius:16px;
      max-width:90%;max-height:140px;overflow:auto;font-size:0.72rem;
      color:#c4001a;text-align:left;white-space:pre-wrap;word-break:break-word;
      font-family:ui-monospace,SFMono-Regular,Menlo,Monaco,monospace;
    ">${escapeHtml(summary).slice(0, 1000)}</pre>
    <div style="display:flex;flex-direction:column;gap:10px;width:min(320px,90%);">
      <button id="ge-restart" type="button" style="
        background:#0385a8;color:#fff;border:none;border-radius:999px;
        padding:14px 18px;font-size:1rem;font-weight:600;cursor:pointer;
      ">Restart app</button>
      ${
        showResetCta
          ? `<button id="ge-reset" type="button" style="
              background:transparent;color:#c4001a;border:1px solid #c4001a;
              border-radius:999px;padding:12px 18px;font-size:0.9rem;font-weight:600;cursor:pointer;
            ">Reset app data and restart</button>`
          : ""
      }
    </div>
  `;

  try {
    document.body.appendChild(overlay);
  } catch {
    // If body itself is broken (very unlikely), give up — at least the
    // log forwarding above already happened.
    fallbackRendered = false;
    return;
  }

  const restartBtn = overlay.querySelector("#ge-restart");
  if (restartBtn) restartBtn.addEventListener("click", softRestart);
  const resetBtn = overlay.querySelector("#ge-reset");
  if (resetBtn) resetBtn.addEventListener("click", fullReset);

  dismissFallbackOnceRootPaints(overlay);
}

// The fallback is only ever shown over a blank root, so the root filling in afterwards means the
// app recovered on its own (a slow boot, or a sign-in that succeeded on retry). Take the overlay
// down again instead of leaving a healthy app hidden behind it.
function dismissFallbackOnceRootPaints(overlay: HTMLElement) {
  let observer: MutationObserver | null = null;
  let poll: ReturnType<typeof setInterval> | null = null;

  const stop = () => {
    if (observer) observer.disconnect();
    if (poll) clearInterval(poll);
    observer = null;
    poll = null;
  };

  const check = () => {
    if (rootIsBlank()) return;
    stop();
    try {
      overlay.remove();
    } catch {
      return;
    }
    fallbackRendered = false;
    pushNative("fallback dismissed: app rendered after the error");
  };

  try {
    const target = document.getElementById("root") || document.body;
    observer = new MutationObserver(check);
    observer.observe(target, {childList: true, subtree: true});
  } catch {
    // MutationObserver unavailable — the poll below still covers it
  }
  try {
    poll = setInterval(check, RECOVERY_POLL_MS);
    setTimeout(stop, RECOVERY_WATCH_MS);
  } catch {
    // setInterval should never throw, but stay defensive
  }
}

function handle(label: string, info: any) {
  const summary = summarize(label, info);
  try {
    console.error("[GlobalErrorHandler]", summary);
  } catch {
    // some Android WebViews proxy console through a native bridge that
    // can throw if the bridge is dead
  }
  pushNative(summary);
  pushDatadog("error", `[GlobalError] ${label}: ${info?.message || ""}`, {error: info});

  // If the React tree never painted (or has wiped itself), give the user
  // a way out. We never auto-render the fallback when the app is healthy,
  // so this won't interrupt a running session for a transient error.
  if (rootIsBlank()) {
    renderFallback(summary);
  }
}

function install() {
  if (installed) return;
  if (typeof window === "undefined") return;
  installed = true;

  try {
    window.addEventListener("error", (event) => {
      const err = event?.error;
      handle("uncaught-error", {
        message: event?.message || err?.message,
        filename: event?.filename,
        lineno: event?.lineno,
        colno: event?.colno,
        stack: err?.stack
      });
    });
  } catch {
    // can't add listener — nothing else to do
  }

  try {
    window.addEventListener("unhandledrejection", (event) => {
      const reason = event?.reason;
      handle("unhandled-rejection", {
        message: typeof reason === "string" ? reason : reason?.message || String(reason || "Unknown"),
        stack: reason?.stack
      });
    });
  } catch {
    // can't add listener — nothing else to do
  }

  try {
    setTimeout(() => {
      if (!fallbackRendered && rootIsBlank()) {
        handle("blank-screen-timeout", {
          message: `App did not render anything within ${BLANK_SCREEN_WATCHDOG_MS}ms of load`
        });
      }
    }, BLANK_SCREEN_WATCHDOG_MS);
  } catch {
    // setTimeout should never throw, but stay defensive
  }

  // If the user just restarted N times in a row, surface the Reset CTA
  // straight away instead of letting them spam Restart.
  if (readRestartCount() >= MAX_RESTARTS_IN_LOOP) {
    handle("restart-loop-detected", {
      message: `Reached ${readRestartCount()} consecutive restarts in ${LOOP_WINDOW_MS / 1000}s`
    });
  }
}

// Auto-install on module evaluation. `src/index.js` imports this as its
// FIRST statement so all subsequent module-level errors are observed.
install();

export {install as installGlobalErrorHandler, softRestart, fullReset};
